import { PageHero } from "@/components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA, OutlineCTA } from "@/components/CTA";
import { Tick, Star } from "@/components/Annotation";
import { SectionLabel, Tape, StickyNote } from "@/components/Notebook";
import { MethodologyTeaser } from "@/components/MethodologyTeaser";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES, BRAND } from "@/config/links";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

// -----------------------------------------------------------------------------
// English-only assets (kept English on purpose, per user request):
//   - Feedback marquee phrases from real workshop participants
//   - Case study stat panel (numbers + labels)
//   - The 3 participant testimonial cards
// -----------------------------------------------------------------------------
const LOGOS = [
  { src: "/logos/amazon.png",  alt: "Amazon",                        w: "w-[150px] md:w-[190px]" },
  { src: "/logos/ert.png",     alt: "ΕΡΤ · Hellenic Broadcasting",   w: "w-[70px]  md:w-[90px]"  },
  { src: "/logos/avocado.png", alt: "Avocado Learning Experiences",  w: "w-[170px] md:w-[210px]" },
];

const FEEDBACK_PHRASES = [
  "Interaction and material discussed",
  "Great facilitator — very good work",
  "The facilitator was very engaging",
  "Interactive activities. Passion. Respect. Politeness.",
  "Great presentation and very helpful",
  "The practical part through the cases",
  "Engaging, to the point and resourceful under different circumstances",
  "Tips for grounding · the energy of the presenter · the actual examples",
  "Interactive — with real examples and scenarios",
  "The role play with the actor and the body exercises",
  "Interactive for most of its duration — a quality often missing from workshops",
  "The interactive exercises and the incorporation of physical practice",
];

const CASE_STATS = [
  { value: "17",     label: "Participants" },
  { value: "100%",   label: "Feedback participation" },
  { value: "5.0/5",  label: "Facilitator rating" },
  { value: "4.8/5",  label: "Workshop rating" },
];

const CASE_QUOTES = [
  { q: "The role play with the actor and the body exercises",                                                                    role: "Participant · Recruitment team" },
  { q: "Engaging, to the point and resourceful under different circumstances",                                                    role: "Participant · Recruitment team" },
  { q: "The workshop was interactive for most of its duration — a quality that is often missing from many workshops",             role: "Participant · Recruitment team" },
];

// -----------------------------------------------------------------------------
// Trust block (uses translated eyebrow strings, English marquee phrases)
// -----------------------------------------------------------------------------
const CorporateTrust = () => {
  const { t } = useLang();
  return (
    <section
      className="relative z-10 py-14 md:py-20 border-y border-ink/10 bg-paper-deep/40"
      data-testid="ct-trust"
    >
      <Reveal className="max-w-[1400px] mx-auto px-5 md:px-10 mb-8 md:mb-10">
        <p className="font-body text-xs uppercase tracking-[0.24em] text-ink-soft text-center">
          {t("corp.trust_label")}
        </p>
      </Reveal>

      <div
        className="max-w-[1400px] mx-auto px-5 md:px-10 flex flex-wrap items-center justify-center gap-12 md:gap-20 mb-10 md:mb-14"
        data-testid="ct-trust-logos"
      >
        {LOGOS.map((l) => (
          <img
            key={l.alt}
            src={l.src}
            alt={l.alt}
            draggable={false}
            className={`${l.w} h-auto opacity-90 hover:opacity-100 transition-opacity duration-500 select-none`}
          />
        ))}
      </div>

      <Reveal className="max-w-[1400px] mx-auto px-5 md:px-10 mb-5 md:mb-6">
        <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta text-center">
          {t("corp.trust_marquee_label")}
        </p>
      </Reveal>

      <div
        className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]"
        data-testid="ct-trust-marquee"
      >
        <div className="flex w-max animate-marquee items-center gap-10 md:gap-14 whitespace-nowrap">
          {[...FEEDBACK_PHRASES, ...FEEDBACK_PHRASES].map((phrase, i) => (
            <span key={i} className="inline-flex items-center gap-10 md:gap-14">
              <span className="font-serif italic text-lg md:text-2xl text-ink/55 hover:text-ink transition-colors duration-500">
                &ldquo;{phrase}&rdquo;
              </span>
              <span
                aria-hidden="true"
                className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta/60"
              />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function CorporateTraining() {
  const { t, l, lang } = useLang();

  const beliefBullets     = t("corp.belief_bullets") || [];
  const programmesItems   = t("corp.programmes_items") || [];
  const focusItems        = t("corp.focus_items") || [];
  const approachSteps     = t("corp.approach_steps") || [];
  const embeddingItems    = t("corp.embedding_items") || [];
  const caseContexts      = t("corp.case_contexts") || [];
  const whyElements       = t("corp.why_elements") || [];
  const outcomesItems     = t("corp.outcomes_items") || [];
  const formatsItems      = t("corp.formats_items") || [];
  const faqItems          = t("corp.faq_items") || [];
  const faqTitleEmphasis  = t("corp.faq_title_emphasis");

  return (
    <>
      <PageMeta
        titleKey="meta.corporate_title"
        descriptionKey="meta.corporate_description"
        path={ROUTES.corporateTraining}
      />
      <PageHero
        testId="ct-hero"
        eyebrow={t("corp.hero_eyebrow")}
        headline={[t("corp.hero_h1_a"), t("corp.hero_h1_b")]}
        emphasis={t("corp.hero_emphasis")}
        emphasisAccent="underline"
        intro={t("corp.hero_intro")}
        // Cormorant Garamond (the shared heading font) has no Greek
        // glyphs, so Greek text in this h1 silently falls back to the
        // browser's default serif font — a DIFFERENT typeface with
        // visibly larger apparent size at the identical declared
        // font-size. Same breakpoint structure/weight/line-height as
        // English, scaled down ~12% to visually match, not literally
        // identical numbers (which wouldn't look the same here).
        headingSizeClassName={
          lang === "el"
            ? "text-[2.2rem] sm:text-[2.6rem] md:text-[3.3rem] lg:text-[4.5rem]"
            : undefined
        }
        meta={
          <>
            {t("corp.hero_meta_line1")}
            <br />
            {t("corp.hero_meta_line2")}
            <br />
            <span className="text-mustard">{t("corp.hero_meta_line3")}</span>
          </>
        }
      >
        <PrimaryCTA href={ROUTES.bookCorporate} testId="ct-book-cta" location="hero" targetAssetId="booking_corporate">
          {t("corp.cta_book")}
        </PrimaryCTA>
        <OutlineCTA href={ROUTES.contact} testId="ct-brief-cta" location="hero" targetAssetId="contact_request">
          {t("corp.cta_brief")}
        </OutlineCTA>
      </PageHero>

      {/* Trust: 3 logos + English feedback marquee */}
      <CorporateTrust />

      {/* Belief — communication shapes how work gets done */}
      <section className="relative z-10 py-24 md:py-32" data-testid="ct-belief">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-7">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.belief_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("corp.belief_h1")}
              <br />
              <span className="italic">{t("corp.belief_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[58ch]">
              {t("corp.belief_body")}
            </p>
            <ul className="mt-8 space-y-3 max-w-[58ch]">
              {beliefBullets.map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <Tick className="w-4 h-4 mt-1 shrink-0" />
                  <span className="font-body text-ink leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 font-hand text-terracotta text-2xl md:text-3xl leading-snug max-w-[58ch]">
              {t("corp.belief_note")}
            </p>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="relative max-w-md mx-auto">
              <Tape className="absolute -top-4 left-6 z-10" rotate={-5} width={110} />
              <Tape className="absolute -top-4 right-6 z-10" rotate={4} width={90} />
              <div className="bg-paper border border-ink/12 shadow-[6px_12px_36px_rgba(27,27,24,0.10)] p-3">
                <img
                  src="/sketches/corp-results.webp"
                  loading="lazy"
                  alt={t("corp.results_alt")}
                  className="w-full h-auto block"
                  style={{ mixBlendMode: "multiply" }}
                  draggable={false}
                  data-testid="ct-belief-image"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Three programme cards */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10"
        data-testid="ct-programmes"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.programmes_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("corp.programmes_h1")}
              <br />
              <span className="italic">{t("corp.programmes_h2")}</span>
            </h2>
          </Reveal>

          <StaggerGroup className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            {programmesItems.map((p) => {
              const href =
                p.key === "pce"     ? ROUTES.presentationExcellence
              : p.key === "epl"     ? ROUTES.executivePresenceForLeaders
              :                       ROUTES.customPrograms;
              return (
                <StaggerItem key={p.key}>
                  <article
                    data-testid={`ct-programme-${p.key}`}
                    className="group relative h-full bg-paper border border-ink/12 shadow-[4px_8px_28px_rgba(27,27,24,0.08)] p-7 md:p-9 flex flex-col transition-shadow duration-300 hover:shadow-[4px_8px_32px_rgba(27,27,24,0.14)] focus-within:shadow-[4px_8px_32px_rgba(27,27,24,0.14)]"
                  >
                    <Tape className="absolute -top-4 left-6" rotate={-6} width={110} />
                    <p className="font-body text-[0.65rem] uppercase tracking-[0.24em] text-terracotta">
                      {p.eyebrow}
                    </p>
                    <h3 className="mt-3 font-serif text-2xl md:text-[1.7rem] text-ink leading-tight">
                      {p.title}
                    </h3>
                    <p className="mt-4 font-hand text-terracotta text-lg leading-snug">
                      {p.lead}
                    </p>
                    <p className="mt-5 font-body text-ink-soft leading-relaxed">{p.body}</p>
                    <SectionLabel className="mt-8 mb-4 block">
                      {t("corp.programmes_outcomes_label")}
                    </SectionLabel>
                    <ul className="space-y-2 flex-1">
                      {p.outcomes.map((o) => (
                        <li key={o} className="flex items-start gap-3">
                          <Tick className="w-4 h-4 mt-1 shrink-0" />
                          <span className="font-body text-ink leading-relaxed">{o}</span>
                        </li>
                      ))}
                    </ul>
                    {/* Visible navigation cue only — NOT its own link (would
                        nest an interactive element inside the full-card
                        overlay link below). Reacts to the card's hover/focus
                        via the `group` on <article> above. */}
                    <span className="mt-8 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.2em] text-ink border-b border-ink/40 pb-1 self-start group-hover:text-terracotta group-hover:border-terracotta group-focus-within:text-terracotta group-focus-within:border-terracotta transition-colors">
                      {t("corp.programmes_cta")}
                      <span>&rarr;</span>
                    </span>
                    {/* Full-card click target — "stretched link" pattern:
                        absolutely positioned over the entire card, placed
                        after all other content so it's the last Tab stop,
                        single semantic link (no nested/duplicate anchors). */}
                    <Link
                      to={l(href)}
                      data-testid={`ct-programme-${p.key}-cta`}
                      aria-label={`${p.title} — ${t("corp.programmes_cta")}`}
                      className="absolute inset-0 z-10 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2"
                    />
                  </article>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* Focus areas */}
      <section className="relative z-10 py-24 md:py-32" data-testid="ct-focus">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.focus_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("corp.focus_h1")}
              <br />
              <span className="italic">{t("corp.focus_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[52ch]">
              {t("corp.focus_body")}
            </p>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7 space-y-6">
            {focusItems.map((f) => (
              <div key={f.title} className="border-t border-ink/15 pt-5">
                <div className="flex items-start gap-4">
                  <Star className="w-4 h-4 mt-1.5 shrink-0" />
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-ink leading-tight">{f.title}</h3>
                    <p className="mt-2 font-body text-ink-soft leading-relaxed">{f.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Approach: 4 steps */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10"
        data-testid="ct-approach"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.approach_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("corp.approach_h1")}
              <br />
              <span className="italic">{t("corp.approach_h2")}</span>
            </h2>
            <p className="mt-8 font-serif italic text-2xl text-ink leading-snug max-w-[46ch]">
              {t("corp.approach_pull")}
            </p>
          </Reveal>

          <StaggerGroup className="lg:col-span-7 space-y-6">
            {approachSteps.map((step, i) => (
              <StaggerItem key={step.label}>
                <div className="flex gap-6 items-start border-t border-ink/15 pt-6">
                  <span className="font-hand text-terracotta text-3xl leading-none shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl text-ink">{step.label}</h3>
                    <p className="mt-2 font-body text-ink-soft leading-relaxed">{step.body}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Why rehearsal works */}
      <section className="relative z-10 py-24 md:py-32" data-testid="ct-rehearsal">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-7 order-2 lg:order-1">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.rehearsal_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("corp.rehearsal_h1")}
              <br />
              <span className="italic">{t("corp.rehearsal_h2")}</span>
            </h2>
            <div className="mt-6 space-y-5 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">
              <p>{t("corp.rehearsal_body_a")}</p>
              <p>{t("corp.rehearsal_body_b")}</p>
            </div>
            <p className="mt-8 font-serif italic text-2xl text-ink leading-snug max-w-[58ch]">
              {t("corp.rehearsal_pull")}
            </p>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative max-w-md mx-auto">
              <Tape className="absolute -top-4 left-6 z-10" rotate={-5} width={90} />
              <Tape className="absolute -top-4 right-6 z-10" rotate={4} width={90} />
              <div className="bg-paper border border-ink/12 shadow-[6px_12px_36px_rgba(27,27,24,0.10)] p-3">
                <img
                  src="/sketches/epd-pressure.webp"
                  loading="lazy"
                  alt={t("corp.pressure_alt")}
                  className="w-full h-auto block"
                  style={{ mixBlendMode: "multiply" }}
                  draggable={false}
                  data-testid="ct-rehearsal-image"
                />
              </div>
              <p className="mt-3 font-hand text-pencil text-lg text-center">
                {t("corp.rehearsal_caption")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Beyond the workshop — embedding */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10"
        data-testid="ct-embedding"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.embedding_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("corp.embedding_h1")}
              <br />
              <span className="italic">{t("corp.embedding_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">
              {t("corp.embedding_body")}
            </p>
          </Reveal>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="relative bg-paper border border-ink/12 shadow-[6px_12px_44px_rgba(27,27,24,0.08)] p-8 md:p-10">
                  <Tape className="absolute -top-4 left-8" rotate={-4} width={110} />
                  <SectionLabel className="mb-6 block">
                    {t("corp.embedding_label")}
                  </SectionLabel>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
                    {embeddingItems.map((d) => (
                      <li key={d} className="flex items-start gap-3">
                        <Tick className="w-4 h-4 mt-1 shrink-0" />
                        <span className="font-body text-ink leading-relaxed">{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:col-span-4">
              <p className="font-serif italic text-2xl md:text-3xl text-ink leading-snug">
                {t("corp.embedding_aim")}
              </p>
              <div className="mt-8">
                <StickyNote rotate={-3} color="#F2EBDA">
                  {t("corp.embedding_note")}
                </StickyNote>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Case study */}
      <section className="relative z-10 py-24 md:py-32" data-testid="ct-case">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.case_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("corp.case_h1")}
              <br />
              <span className="italic">{t("corp.case_h2")}</span>
            </h2>
          </Reveal>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7 space-y-6 font-body text-lg text-ink-soft leading-relaxed">
              <Reveal>
                <p>
                  {t("corp.case_p1_a")}
                  <span className="font-medium text-ink">{t("corp.case_partner")}</span>
                  {t("corp.case_p1_b")}
                  <em>{t("corp.case_focus_question")}</em>
                </p>
                <p>{t("corp.case_p2")}</p>
                <p className="font-hand text-terracotta text-2xl md:text-3xl leading-snug not-italic">
                  {t("corp.case_four_elements")}
                </p>
                <p>{t("corp.case_p3")}</p>
                <ul className="space-y-2 pl-1">
                  {caseContexts.map((c) => (
                    <li key={c} className="flex items-start gap-3">
                      <Tick className="w-4 h-4 mt-1 shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
                <p className="font-serif italic text-2xl text-ink leading-snug not-italic">
                  {t("corp.case_pull")}
                </p>
              </Reveal>
            </div>

            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="relative">
                <Tape className="absolute -top-4 left-6 z-10" rotate={-5} width={110} />
                <Tape className="absolute -top-4 right-6 z-10" rotate={4} width={90} />
                <div className="bg-paper border border-ink/12 shadow-[8px_16px_56px_rgba(27,27,24,0.12)] p-3 md:p-4">
                  <img
                    src="/sketches/ld-collaboration.webp"
                    loading="lazy"
                    alt={t("corp.collaboration_alt")}
                    className="w-full h-auto block"
                    style={{ mixBlendMode: "multiply" }}
                    draggable={false}
                    data-testid="ct-case-image"
                  />
                </div>
                <p className="mt-3 font-hand text-pencil text-lg text-center">
                  {t("corp.case_image_caption")}
                </p>
              </div>
            </Reveal>
          </div>

          {/* Ratings stat panel — kept English */}
          <Reveal className="mt-16 md:mt-20">
            <div className="relative bg-ink text-paper px-6 md:px-12 py-12 md:py-14">
              <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-8 text-center">
                {t("corp.case_ratings_label")}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6">
                {CASE_STATS.map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="font-serif text-5xl md:text-6xl lg:text-7xl text-mustard leading-none">
                      {s.value}
                    </p>
                    <p className="mt-3 font-body text-xs md:text-sm uppercase tracking-[0.2em] text-paper/80">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
              <p className="mt-10 font-body text-sm md:text-base text-paper/80 max-w-3xl mx-auto text-center leading-relaxed">
                {t("corp.case_ratings_summary")}
              </p>
            </div>
          </Reveal>

          {/* Selected participant quotes — kept English */}
          <Reveal className="mt-14 md:mt-16">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-8 text-center">
              In their own words
            </p>
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {CASE_QUOTES.map((c, i) => (
                <figure
                  key={i}
                  className="relative bg-paper border border-ink/12 p-7 md:p-8 shadow-[4px_8px_24px_rgba(27,27,24,0.06)]"
                >
                  <span
                    aria-hidden="true"
                    className="absolute -top-4 -left-1 font-serif text-6xl text-terracotta/50 leading-none"
                  >
                    &ldquo;
                  </span>
                  <blockquote className="font-serif italic text-lg md:text-xl text-ink leading-snug">
                    {c.q}
                  </blockquote>
                  <figcaption className="mt-4 font-hand text-pencil text-base">
                    — {c.role}
                  </figcaption>
                </figure>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-12 text-center">
            <p className="font-body text-sm text-ink-soft italic max-w-2xl mx-auto">
              {t("corp.case_signature")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Why InterAct */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10"
        data-testid="ct-why"
      >
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-4 order-2 lg:order-1">
            <div className="relative max-w-[280px] mx-auto lg:mx-0">
              <Tape className="absolute -top-3 left-6 z-10" rotate={-5} width={80} />
              <Tape className="absolute -top-3 right-6 z-10" rotate={4} width={80} />
              <div className="aspect-[4/5] bg-paper border border-ink/12 overflow-hidden shadow-[6px_12px_36px_rgba(27,27,24,0.10)]">
                <img
                  src="/sketches/coach-portrait.webp"
                  srcSet="/sketches/coach-portrait-500.webp 500w, /sketches/coach-portrait.webp 900w"
                  sizes="(min-width: 1024px) 40vw, 90vw"
                  loading="lazy"
                  alt={t("about.portrait_alt", { name: BRAND.coach })}
                  className="w-full h-full object-cover object-[center_top]"
                  style={{ mixBlendMode: "multiply", filter: "contrast(1.05)" }}
                  draggable={false}
                  data-testid="ct-coach-portrait"
                />
              </div>
              <p className="mt-3 font-hand text-terracotta text-lg text-center lg:text-left">
                {BRAND.coach}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-8 order-1 lg:order-2">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.why_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("corp.why_h1")}
              <br />
              <span className="italic">{t("corp.why_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">
              {t("corp.why_body")}
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-y-2 gap-x-6 max-w-[520px]">
              {whyElements.map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <Tick className="w-4 h-4 mt-1 shrink-0" />
                  <span className="font-body text-ink leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 font-serif italic text-2xl text-ink leading-snug max-w-[58ch]">
              {t("corp.why_pull")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Business outcomes */}
      <section className="relative z-10 py-24 md:py-32" data-testid="ct-outcomes">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.outcomes_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("corp.outcomes_h1")}
              <br />
              <span className="italic">{t("corp.outcomes_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[52ch]">
              {t("corp.outcomes_intro")}
            </p>

            <div className="mt-8 relative max-w-[320px]">
              <Tape className="absolute -top-3 left-6 z-10" rotate={-6} width={80} />
              <div className="bg-paper border border-ink/12 shadow-[4px_8px_24px_rgba(27,27,24,0.08)] p-2">
                <img
                  src="/sketches/corp-outcomes.webp"
                  loading="lazy"
                  alt={t("corp.outcomes_alt")}
                  className="w-full h-auto block"
                  style={{ mixBlendMode: "multiply" }}
                  draggable={false}
                  data-testid="ct-outcomes-image"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7 space-y-4">
            {outcomesItems.map((o, i) => (
              <div key={o} className="flex items-start gap-5 border-t border-ink/15 pt-4">
                <span className="font-hand text-terracotta text-2xl leading-none shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-body text-lg text-ink leading-relaxed">{o}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Formats */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10"
        data-testid="ct-formats"
      >
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.formats_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("corp.formats_h1")}
              <br />
              <span className="italic">{t("corp.formats_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">
              {t("corp.formats_body")}
            </p>
          </Reveal>

          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {formatsItems.map((f, i) => (
              <li key={f} className="relative bg-paper border border-ink/12 p-6 flex items-start gap-4">
                <span className="font-hand text-terracotta text-xl leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-serif text-lg md:text-xl text-ink leading-snug">{f}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Methodology teaser */}
      <MethodologyTeaser testId="ct-methodology-teaser" />

      {/* Final CTA */}
      <section
        className="relative z-10 py-24 md:py-32 bg-ink text-paper"
        data-testid="ct-final-cta"
      >
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-6">
              {t("corp.final_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-paper">
              {t("corp.final_h1")}
              <br />
              <span className="italic">{t("corp.final_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-paper/90 max-w-2xl mx-auto leading-relaxed">
              {t("corp.final_body")}
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex justify-center gap-5 flex-wrap">
            <PrimaryCTA href={ROUTES.bookCorporate} testId="ct-final-book-cta" location="final_cta" targetAssetId="booking_corporate">
              {t("corp.final_cta_book")}
            </PrimaryCTA>
            <OutlineCTA
              href={ROUTES.contact}
              testId="ct-final-brief-cta"
              location="final_cta"
              targetAssetId="contact_request"
              className="!border-paper/70 !text-paper hover:!border-mustard hover:!text-mustard"
            >
              {t("corp.final_cta_brief")}
            </OutlineCTA>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        className="relative z-10 py-24 md:py-36 bg-paper-deep/40"
        data-testid="ct-faq"
      >
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("corp.faq_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              {t("corp.faq_title")}
              {faqTitleEmphasis && (
                <>
                  <br />
                  <span className="italic">{faqTitleEmphasis}</span>
                </>
              )}
            </h2>
          </Reveal>

          <Reveal>
            <Accordion
              type="single"
              collapsible
              className="w-full border-t border-ink/15"
              data-testid="ct-faq-accordion"
            >
              {faqItems.map((f, i) => (
                <AccordionItem key={i} value={`ct-item-${i}`} className="border-ink/15">
                  <AccordionTrigger
                    data-testid={`ct-faq-trigger-${i}`}
                    className="font-serif text-xl md:text-2xl text-ink hover:no-underline py-6 [&>svg]:text-terracotta [&>svg]:h-5 [&>svg]:w-5 text-left"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="font-body text-base md:text-lg text-ink-soft leading-relaxed max-w-2xl">
                      {f.a}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
